import {
    UiTrelloBoardModel,
    UiTrelloCardModel,
    UiTrelloListModel
} from "../../models";
import { getColorsFromBackgroundField } from "../../styles";

import { CheckListIcon, TextIcon, TimerIcon } from "./icons";
import {
    MobileClippingMask,
    MobilePhone,
    MobileForehead,
    MobileScreen,
    MobileButtons,
    MobileBoard,
    MobileHeader,
    Board,
    BoardName,
    Lists,
    List,
    ListContent,
    ListName,
    Card,
    CardImage,
    CardContent,
    Labels,
    Label,
    Description,
    Users,
    User
} from "./UiTrelloBoard.styles";

export type TrelloBoardProps = {
    readonly model: UiTrelloBoardModel;
};

const UiTrelloBoard = ({ model }: TrelloBoardProps) => {
    const { layout, name, lists, background } = model.content;
    if (layout === "Mobile") {
        return (
            <MobileClippingMask>
                <MobilePhone>
                    <MobileForehead>
                        <div />
                    </MobileForehead>
                    <MobileButtons>
                        <div className="silent" />
                        <div className="volume" />
                        <div className="lock" />
                    </MobileButtons>
                    <MobileScreen>
                        <MobileBoard
                            {...getColorsFromBackgroundField(background)}
                        >
                            {name && (
                                <MobileHeader
                                    {...getColorsFromBackgroundField(
                                        background
                                    )}
                                >
                                    <h3>{name}</h3>
                                </MobileHeader>
                            )}
                            {lists && <TrelloLists lists={lists} />}
                        </MobileBoard>
                    </MobileScreen>
                </MobilePhone>
            </MobileClippingMask>
        );
    } else {
        return (
            <Board {...getColorsFromBackgroundField(background)}>
                {name && (
                    <BoardName {...getColorsFromBackgroundField(background)}>
                        <h3>{name}</h3>
                    </BoardName>
                )}
                {lists && <TrelloLists lists={lists} />}
            </Board>
        );
    }
};

const TrelloLists = ({ lists }: { lists: readonly UiTrelloListModel[] }) => {
    return (
        <Lists>
            {lists
                .filter(({ type }) => type === "uiTrelloList")
                .map((list) => (
                    <List key={list.id}>
                        <ListContent>
                            <ListName>{list.content.name}</ListName>
                            {list.content.cards
                                ?.filter(({ type }) => type === "uiTrelloCard")
                                .map((card: UiTrelloCardModel) => (
                                    <Card key={card.id}>
                                        {!!card.content.image && (
                                            <CardImage
                                                src={card.content.image.url}
                                                alt={card.content.image.title}
                                            />
                                        )}
                                        <CardContent>
                                            {!!card.content.label && (
                                                <Labels>
                                                    <Label
                                                        color={
                                                            card.content.label
                                                        }
                                                    />
                                                </Labels>
                                            )}
                                            {card.content.name}
                                            {!!card.content.hasDescription ? (
                                                <Description>
                                                    <TextIcon />
                                                </Description>
                                            ) : (
                                                <CardDescription>
                                                    {
                                                        card.content
                                                            .descriptionType
                                                    }
                                                </CardDescription>
                                            )}
                                            {!!card.content.users && (
                                                <Users>
                                                    {card.content.users.map(
                                                        (user) => (
                                                            <User
                                                                src={user.url}
                                                                width={
                                                                    user.width
                                                                }
                                                                height={
                                                                    user.height
                                                                }
                                                                alt={user.title}
                                                                key={user.url}
                                                            />
                                                        )
                                                    )}
                                                </Users>
                                            )}
                                        </CardContent>
                                    </Card>
                                ))}
                        </ListContent>
                    </List>
                ))}
        </Lists>
    );
};

const CardDescription = ({
    children
}: {
    children: UiTrelloCardModel["content"]["descriptionType"];
}) => {
    switch (children) {
        case "CheckList":
            return (
                <Description>
                    <CheckListIcon />
                    <span>0/3</span>
                </Description>
            );
        case "CheckListInProgress":
            return (
                <Description>
                    <CheckListIcon />
                    <span>2/5</span>
                </Description>
            );
        case "CheckListDone":
            return (
                <Description>
                    <CheckListIcon />
                    <span>7/7</span>
                </Description>
            );
        case "Text":
            return (
                <Description>
                    <TextIcon />
                </Description>
            );
        case "Timer":
            return (
                <Description>
                    <TimerIcon />
                </Description>
            );
        default:
            return null;
    }
};

export default UiTrelloBoard;
